import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { IOptionItem } from "@/components/ui/dropdown/types";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import { GeneralSummary } from "@/types/analysisSummary";

const SCORE_ATTRIBUTE: IOptionItem = { label: "Score", value: "Score" };

export interface AnalysisSummaryState {
  generalSummary: GeneralSummary | null;
  spatialAnalysisDatasetId: string | null;
  selectedAttribute: IOptionItem;

  isHasSummary: boolean;
  gridsSelected: string[];
}

export const analysisSummaryInitialState: AnalysisSummaryState = {
  generalSummary: null,
  spatialAnalysisDatasetId: null,
  isHasSummary: false,
  selectedAttribute: SCORE_ATTRIBUTE,
  gridsSelected: [DROPDOWN_ALL_VALUE]
};

// Get default attribute for summary when the summary is empty or has no attributes
const getDefaultAttribute = (
  generalSummary: GeneralSummary | null,
  isHasSummary: boolean
): IOptionItem => {
  if (isHasSummary || !generalSummary) {
    return SCORE_ATTRIBUTE;
  }

  const filteredAttribute = generalSummary?.data.attributes.filter(
    (attr) => attr.attribute_name !== "Score"
  );

  const firstAttribute = filteredAttribute[0];

  if (firstAttribute) {
    return {
      label: firstAttribute.attribute_name,
      value: firstAttribute.attribute_name
    };
  }

  return SCORE_ATTRIBUTE;
};

export const analysisSummarySlice = createSlice({
  name: "analysisSummary",
  initialState: analysisSummaryInitialState,
  reducers: {
    setGeneralSummary: (
      state,
      action: PayloadAction<GeneralSummary | null>
    ) => {
      state.generalSummary = action.payload;
      state.selectedAttribute = getDefaultAttribute(
        action.payload,
        state.isHasSummary
      );
    },
    setSpatialAnalysisDatasetId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.spatialAnalysisDatasetId = action.payload;
    },
    setIsHasSummary: (state, action: PayloadAction<boolean>) => {
      state.isHasSummary = action.payload;
    },
    addGridSelected: (state, action: PayloadAction<string>) => {
      let grids = state.gridsSelected?.filter(
        (item) => item !== DROPDOWN_ALL_VALUE
      );
      if (grids.includes(action.payload)) {
        grids = grids.filter((item) => item !== action.payload);
        state.gridsSelected = grids.length > 0 ? grids : [DROPDOWN_ALL_VALUE];
      } else if (grids.length < 3)
        state.gridsSelected = [...grids, action.payload];
    },
    setGridsSelected: (state, action: PayloadAction<string[]>) => {
      const grids = action.payload;
      if (grids.length === 1 && grids.includes(DROPDOWN_ALL_VALUE)) {
        state.gridsSelected = grids;
      } else {
        state.gridsSelected = action.payload?.filter(
          (item) => item !== DROPDOWN_ALL_VALUE
        );
      }
    },
    setSelectedAttribute: (state, action: PayloadAction<IOptionItem>) => {
      state.selectedAttribute = action.payload;
    },
    resetGridsSelected: (state) => {
      state.gridsSelected = [DROPDOWN_ALL_VALUE];
    },
    resetAnalysisSummaryState: () => analysisSummaryInitialState
  }
});

export const {
  setGeneralSummary,
  setIsHasSummary,
  setSelectedAttribute,
  addGridSelected,
  setGridsSelected,
  resetGridsSelected,
  setSpatialAnalysisDatasetId,
  resetAnalysisSummaryState
} = analysisSummarySlice.actions;

export const analysisSummaryReducer = analysisSummarySlice.reducer;
